import api from './api';

export interface DataSourceSchedule {
  id: string;
  name: string;
  source_type: string;
  sync_enabled: boolean;
  health_status: 'healthy' | 'warning' | 'error' | 'disconnected';
  last_sync_at?: string;
  next_sync_at?: string;
  sync_frequency: string;
  sync_frequency_description: string;
  created_at: string;
  updated_at: string;
}

export interface SyncFrequencyOption {
  value: string;
  label: string;
  description: string;
}

export interface SchedulesResponse {
  schedules: DataSourceSchedule[];
  total: number;
  active: number;
}

export interface UpdateScheduleRequest {
  sync_enabled?: boolean;
  sync_frequency?: string;
}

export interface UpdateScheduleResponse {
  id: string;
  sync_enabled: boolean;
  sync_frequency?: string;
  sync_frequency_description: string;
  updated_at: string;
}

export interface ManualSyncResponse {
  message: string;
  data_source_id: string;
  triggered_at: string;
}

export const schedulesApi = {
  /**
   * Get all data source schedules
   */
  async getSchedules(): Promise<SchedulesResponse> {
    const response = await api.get('/api/integrations/schedules');
    return response.data;
  },

  /**
   * Get available sync frequency options
   */
  async getSyncFrequencyOptions(): Promise<{ options: SyncFrequencyOption[] }> {
    const response = await api.get('/api/integrations/schedules/frequency-options');
    return response.data;
  },

  /**
   * Update data source sync settings
   */
  async updateSchedule(dataSourceId: string, settings: UpdateScheduleRequest): Promise<UpdateScheduleResponse> {
    const response = await api.put(`/api/integrations/data-sources/${dataSourceId}/schedule`, settings);
    return response.data;
  },

  /**
   * Trigger manual sync for a data source
   */
  async triggerManualSync(dataSourceId: string): Promise<ManualSyncResponse> {
    const response = await api.post(`/api/integrations/data-sources/${dataSourceId}/sync/manual`);
    return response.data;
  },

  /**
   * Enable/disable sync for a data source
   */
  async toggleSync(dataSourceId: string, enabled: boolean): Promise<UpdateScheduleResponse> {
    return this.updateSchedule(dataSourceId, { sync_enabled: enabled });
  },

  /**
   * Update sync frequency for a data source
   */
  async updateSyncFrequency(dataSourceId: string, frequency: string): Promise<UpdateScheduleResponse> {
    return this.updateSchedule(dataSourceId, { sync_frequency: frequency });
  }
};

export default schedulesApi; 